import urljoin from 'url-join';
import {
	API_ENDPOINT_LAB_VANTAGE_FILES_IMPORT,
	API_ENDPOINT_LAB_VANTAGE_FILES_NOTICE_ACKNOWLEDGE,
	API_ENDPOINT_LAB_VANTAGE_FILES_SUMMARY,
} from '@/config/env.config';
import { portalApi } from './index.api';

/** Stable RTK endpoint keys on `portalApi.endpoints` after `adminFilesApi` injection. */
export const LAB_VANTAGE_ADMIN_ENDPOINT = Object.freeze({
	summary: 'adminLabVantageSummary',
	import: 'adminLabVantageImport',
	noticeAcknowledge: 'adminLabVantageNoticeAcknowledge',
});

/** Full URL including `projectId` query for Lab Vantage summary (matches RTK `adminLabVantageSummary`). */
export function buildLabVantageSummaryUrl(projectId) {
	const sp = new URLSearchParams();
	sp.append('projectId', String(projectId));
	return `${API_ENDPOINT_LAB_VANTAGE_FILES_SUMMARY}?${sp.toString()}`;
}

export const filesApi = portalApi.injectEndpoints({
	endpoints: (builder) => ({
		files: builder.query({
			query: (query) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("files", "project", query),
				method: 'GET',
			}),
			providesTags: ['files']
		}),
		getFile: builder.query({
			query: (projectId, fileId) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("files", "download", projectId, fileId),
				method: 'GET',
			})
		}),
		getFilesById: builder.query({
			query: (projectId, fileIds) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("files", "download", projectId, 'fileids'),
				method: 'POST',
				body: fileIds
			})
		}),
		getAllFiles: builder.query({
			query: (projectId) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("files", "download", projectId),
				method: 'GET',
			})
		}),
	})
});

export const {
	useFilesQuery,
	useGetFileQuery,
	useGetFilesByIdQuery,
	useGetAllFilesQuery
} = filesApi;

/* 
√ GET /api/v1/files/project/{projectId}
√ GET /api/v1/files/download/{projectId}/{fileId}
√ POST /api/v1/files/download/{projectId}/fileIds
√ GET /api/v1/files/download/{projectId}
GET /api/v1/files/project/{projectId}/search/{searchText}
POST /api/v1/files/download/{projectId}
*/

export const adminFilesApi = portalApi.injectEndpoints({
	endpoints: (builder) => ({
		adminFiles: builder.query({
			query: (query) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "files", "project", query),
				method: 'GET',
			}),
			providesTags: ['admin-files']
		}),
		adminGetFile: builder.query({
			query: (projectId, fileId) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "files", "download", projectId, fileId),
				method: 'GET',
			})
		}),
		adminSaveFiles: builder.mutation({
			query: (files) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "files", "publish", "collection"),
				method: 'PUT',
				body: {
					instance: files,
					comment: "PortalUI: Update from portal"
				}
			}),
			invalidatesTags: ['files', 'admin-files']
		}),

		adminSaveFile: builder.mutation({
			query: (file) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "files", "publish", "single"),
				method: 'PUT',
				body: {
					instance: file,
					comment: "PortalUI: Update from portal"
				}
			}),
			invalidatesTags: ['files', 'admin-files']
		}),

		adminRemoveFile: builder.mutation({
			query: (file) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "files", "remove", "single"),
				method: 'PUT',
				body: {
					instance: file,
					comment: "PortalUI: Update from portal"
				}
			}),
			invalidatesTags: ['files', 'admin-files']
		}),

		adminFileProject: builder.mutation({
			query: (files) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "files", "publish", "project"),
				method: 'PUT',
				body: {
					instance: files,
					comment: "PortalUI: Update from portal"
				}
			}),
			invalidatesTags: ['files', 'admin-files']
		}),
		adminUploadFile: builder.mutation({
			query: (projectId, file, comment) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "files", "upload", "single", projectId),
				method: 'POST',
				body: {
					instance: [...file],
					comment
				}
			}),
			invalidatesTags: ['files', 'admin-files']
		}),

		/** GET /api/v1/admin/lab-vantage/files/summary?projectId= */
		adminLabVantageSummary: builder.query({
			query: (projectId) => ({
				url: buildLabVantageSummaryUrl(projectId),
				method: 'GET',
			}),
			providesTags: (result, error, projectId) =>
				projectId == null ? [] : [{ type: 'labVantageSummary', id: String(projectId) }],
		}),

		/** POST /api/v1/admin/lab-vantage/files/import */
		adminLabVantageImport: builder.mutation({
			query: ({ projectId, lvFileIds, acknowledgeNotice }) => ({
				url: API_ENDPOINT_LAB_VANTAGE_FILES_IMPORT,
				method: 'POST',
				body: {
					projectId,
					lvFileIds,
					acknowledgeNotice,
				},
			}),
			invalidatesTags: (result, error, arg) => [
				...(arg?.projectId != null ? [{ type: 'labVantageSummary', id: String(arg.projectId) }] : []),
				'files',
				'admin-files',
			],
		}),

		/** POST /api/v1/admin/lab-vantage/files/notice/acknowledge (204 No Content) */
		adminLabVantageNoticeAcknowledge: builder.mutation({
			query: ({ projectId }) => ({
				url: API_ENDPOINT_LAB_VANTAGE_FILES_NOTICE_ACKNOWLEDGE,
				method: 'POST',
				body: { projectId },
			}),
			invalidatesTags: (result, error, arg) =>
				arg?.projectId != null ? [{ type: 'labVantageSummary', id: String(arg.projectId) }] : [],
		}),
	})
});

export const {
	useAdminFilesQuery,
	useAdminGetFileQuery,
	useAdminSaveFileMutation,
	useAdminRemoveFileMutation,
	useAdminSaveFilesMutation,
	useAdminUploadFileMutation,
	useAdminFileProjectMutation,
	useAdminLabVantageSummaryQuery,
	useLazyAdminLabVantageSummaryQuery,
	useAdminLabVantageImportMutation,
	useAdminLabVantageNoticeAcknowledgeMutation,
} = adminFilesApi;

/*
GET /api/v1/admin/files/{projectId}/{fileId}
√ GET /api/v1/admin/files/project/{projectId}
√ PUT /api/v1/admin/files/publish/project
√ PUT /api/v1/admin/files/publish/single
√ PUT /api/v1/admin/files/publish/collection
√ POST /api/v1/admin/files/upload/single/{projectId}
√ GET /api/v1/admin/files/download/{projectId}/{fileId}
√ PUT /api/v1/admin/files/remove/single
GET /api/v1/admin/lab-vantage/files/summary?projectId=
POST /api/v1/admin/lab-vantage/files/import
POST /api/v1/admin/lab-vantage/files/notice/acknowledge
 */